import React, { useState, useEffect } from 'react';
import { CurrencyRupeeIcon, UserGroupIcon, EyeIcon } from '@heroicons/react/24/outline';
import { createColumnHelper } from '@tanstack/react-table';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';
import vegetablesService from '../../services/vegetablesService';

function SupplierDuesModal({ isOpen, onClose, supplier }) {
  if (!supplier) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${supplier.supplierName}'s Outstanding Payments`}
    >
      <div className="space-y-4">
        {/* Supplier Details */}
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-2">
          <p className="text-sm text-gray-600 dark:text-gray-300 flex justify-between">
            <span>Supplier:</span>
            <span className="font-medium">{supplier.supplierName}</span>
          </p>
          {supplier.supplierDetails?.phone && (
            <p className="text-sm text-gray-600 dark:text-gray-300 flex justify-between">
              <span>Phone:</span>
              <a 
                href={`tel:${supplier.supplierDetails.phone}`}
                className="font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
              >
                {supplier.supplierDetails.phone}
              </a>
            </p>
          )}
          <p className="text-sm text-gray-600 dark:text-gray-300 flex justify-between">
            <span>Total Outstanding:</span>
            <span className="font-medium text-red-600 dark:text-red-400">
              ₹{supplier.totalPending.toLocaleString()}
            </span>
          </p>
        </div>

        {/* Purchases List */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white">
            Outstanding Purchases
          </h4>
          {supplier.purchases.map((purchase) => (
            <div 
              key={purchase.id}
              className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3"
            >
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {new Date(purchase.createdAt).toLocaleDateString()}
                </span>
              </div>
              
              <div className="space-y-2">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {purchase.items.map(item => 
                    `${item.vegetable} (${item.quantity}kg)`
                  ).join(', ')}
                </div>
                
                <div className="border-t border-gray-200 dark:border-gray-600 pt-2 space-y-1">
                  <p className="text-sm text-gray-600 dark:text-gray-300 flex justify-between">
                    <span>Total Amount:</span>
                    <span className="font-medium">₹{purchase.totalAmount?.toLocaleString()}</span>
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 flex justify-between">
                    <span>Amount Paid:</span>
                    <span className="font-medium">₹{purchase.amountPaid?.toLocaleString()}</span>
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 flex justify-between">
                    <span>Balance:</span>
                    <span className="font-medium text-red-600 dark:text-red-400">
                      ₹{purchase.remainingBalance?.toLocaleString()}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}

export default function PendingPayments({ purchases, supplierDetails }) {
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [vegetables, setVegetables] = useState({});

  // Fetch vegetables on component mount
  useEffect(() => {
    const fetchVegetables = async () => {
      try {
        const veggies = await vegetablesService.getVegetables();
        const veggiesMap = {};
        veggies.forEach(veg => {
          veggiesMap[veg.id] = veg;
        });
        setVegetables(veggiesMap);
      } catch (error) {
        console.error('Error fetching vegetables:', error);
      }
    };
    fetchVegetables();
  }, []);

  // Group purchases by supplier
  const supplierBalances = purchases.reduce((acc, purchase) => {
    const supplierId = purchase.supplierId;
    const remainingBalance = Number(purchase.remainingBalance) || 0;

    if (remainingBalance <= 0) return acc;

    if (!acc[supplierId]) {
      acc[supplierId] = {
        supplierId,
        supplierName: purchase.supplierName,
        supplierDetails: supplierDetails[supplierId],
        totalPending: 0,
        purchases: []
      };
    }
    
    acc[supplierId].totalPending += remainingBalance;
    acc[supplierId].purchases.push({
      ...purchase,
      items: purchase.items.map(item => ({
        ...item,
        vegetable: vegetables[item.vegetableId]?.name || 'Unknown'
      }))
    });
    return acc;
  }, {});

  const data = Object.values(supplierBalances);

  // Calculate summary statistics
  const totalPendingAmount = data.reduce((sum, supplier) => sum + supplier.totalPending, 0);
  const totalSuppliers = data.length;

  const stats_cards = [
    {
      name: 'Outstanding Amount',
      value: `₹${totalPendingAmount.toLocaleString()}`,
      icon: CurrencyRupeeIcon,
      bgColor: 'bg-red-500'
    },
    {
      name: 'Suppliers to Pay',
      value: totalSuppliers,
      icon: UserGroupIcon,
      bgColor: 'bg-yellow-500'
    }
  ];

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor('supplierName', {
      header: 'Supplier',
      cell: info => info.getValue(),
    }),
    columnHelper.accessor('totalPending', {
      header: () => <div className="text-left">Amount Due</div>,
      cell: info => (
        <div className="text-left font-semibold text-red-600 dark:text-red-400">
          ₹{info.getValue().toLocaleString()}
        </div>
      ),
    }),
    columnHelper.accessor('purchases', {
      header: 'Actions',
      cell: info => (
        <button
          onClick={() => setSelectedSupplier(info.row.original)}
          className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded 
            text-primary-700 bg-primary-100 hover:bg-primary-200 
            dark:text-primary-400 dark:bg-primary-900/50 dark:hover:bg-primary-900"
        >
          <EyeIcon className="h-4 w-4 mr-1" />
          View Details
        </button>
      ),
    }),
  ];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {stats_cards.map((stat) => (
          <div
            key={stat.name}
            className="relative overflow-hidden rounded-lg bg-white dark:bg-[#2E3B4E] p-3 sm:p-4 shadow"
          >
            <dt>
              <div className={`absolute rounded-md ${stat.bgColor} p-2 sm:p-3`}>
                <stat.icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" aria-hidden="true" />
              </div>
              <p className="ml-12 sm:ml-16 truncate text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">
                {stat.name}
              </p>
            </dt>
            <dd className="ml-12 sm:ml-16 flex items-baseline">
              <p className="text-lg sm:text-2xl font-semibold text-gray-900 dark:text-white">
                {stat.value}
              </p>
            </dd>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-[#2E3B4E] shadow rounded-lg overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="sm:flex sm:justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Outstanding Payments
            </h2>
          </div>
        </div>

        <DataTable
          data={data}
          columns={columns}
          sorting={sorting}
          setSorting={setSorting}
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          searchPlaceholder="Search suppliers..."
        />
      </div>

      <SupplierDuesModal 
        isOpen={!!selectedSupplier}
        onClose={() => setSelectedSupplier(null)}
        supplier={selectedSupplier}
      />
    </div>
  );
}
